import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Runs an async loader and tracks {data, error, loading}.
 * Keeps stale data visible while refetching, so lists do not flash empty.
 */
export default function useAsync(loader, deps = [], { immediate = true, keepPrevious = true } = {}) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(immediate);
  const mounted = useRef(true);
  const runId = useRef(0);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const run = useCallback(
    async (...args) => {
      const id = (runId.current += 1);
      setLoading(true);
      setError(null);
      if (!keepPrevious) setData(null);
      try {
        const result = await loader(...args);
        if (mounted.current && id === runId.current) {
          setData(result);
          setLoading(false);
        }
        return result;
      } catch (err) {
        if (err?.name === 'AbortError') return null;
        if (mounted.current && id === runId.current) {
          setError(err);
          setLoading(false);
        }
        throw err;
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    deps,
  );

  useEffect(() => {
    if (!immediate) return;
    run().catch(() => {
      /* surfaced through `error` */
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return { data, error, loading, run, reload: run, setData };
}
